/**
 * "سجلات خارجية" — documents the patient brings in from outside the platform.
 *
 * Everything else in the medical record is generated here: a booking, a
 * prescription, a result uploaded against a test this clinic asked for. But
 * most people arrive with a history — scans, lab reports and old prescriptions
 * from clinics that never used this system. Without somewhere to put those, the
 * "medical record" only covers the part of a life that happened after signing
 * up, and the printed copy a patient hands to another doctor is missing
 * everything that came before.
 *
 * An old document can be tied to a visit that happened here ("this is the x-ray
 * Dr X asked for last March"), which is optional because most of them predate
 * any booking on this platform.
 */

import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Icon,
  Input,
  Modal,
  Panel,
  Select,
  Spinner,
  Textarea,
  DateInput,
} from '../../components/common';
import { MedicalFileViewer } from '../../components/common/MedicalFileViewer';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useAsync } from '../../hooks/useAsync';
import { usePatientBookings } from '../../hooks/useBookings';
import { medicalRecordApi } from '../../api/endpoints';
import { compressImage, readFileAsDataUri, formatBytes, dataUriByteLength } from '../../domain/files';
import { isoToDisplayDate, todayKey } from '../../domain/dates';

/** Matches the recordType values the backend accepts. */
export const RECORD_TYPES = [
  { value: 'lab', label: 'تحليل', icon: 'flask-conical', tone: 'text-purple-300' },
  { value: 'xray', label: 'أشعة', icon: 'scan', tone: 'text-amber-300' },
  { value: 'prescription', label: 'روشتة', icon: 'file-text', tone: 'text-cyan-300' },
  { value: 'report', label: 'تقرير طبي', icon: 'clipboard-list', tone: 'text-emerald-300' },
  { value: 'vaccination', label: 'تطعيم', icon: 'heart-pulse', tone: 'text-rose-300' },
  { value: 'surgery', label: 'عملية', icon: 'activity', tone: 'text-indigo-300' },
  { value: 'other', label: 'مستند آخر', icon: 'paperclip', tone: 'text-slate-300' },
];

const typeMeta = (value) =>
  RECORD_TYPES.find((t) => t.value === value) || RECORD_TYPES[RECORD_TYPES.length - 1];

const MAX_FILES = 12;
/** Matches the backend's per-file ceiling, so a rejection happens before upload. */
const MAX_FILE_BYTES = 6 * 1024 * 1024;

const emptyForm = () => ({
  id: null,
  title: '',
  recordType: 'lab',
  recordDate: todayKey(),
  externalDoctor: '',
  facility: '',
  bookingId: '',
  notes: '',
  files: [],
});

/**
 * `onChange` runs after a record is saved or deleted: the medical record page
 * keeps its own copy of the list for the tab count and the printed record.
 */
export function ExternalRecords({ onChange }) {
  const { patientId } = useAuth();
  const { success, error: toastError } = useNotifications();

  const state = useAsync(() => medicalRecordApi.list(), [patientId], {
    initialData: [],
    enabled: Boolean(patientId),
  });

  const { bookings } = usePatientBookings(patientId, { poll: false });

  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [viewingFile, setViewingFile] = useState(null);

  const records = useMemo(() => state.data || [], [state.data]);

  /** Past visits, newest first — what an old document can be attached to. */
  const visitOptions = useMemo(
    () =>
      [...bookings]
        .filter((b) => b.status !== 'cancelled')
        .sort((a, b) => String(b.date).localeCompare(String(a.date)))
        .map((b) => ({
          value: String(b.id),
          label: `${isoToDisplayDate(b.date) || b.date} — ${b.doctorName || 'زيارة'}${
            b.clinicName ? ` (${b.clinicName})` : ''
          }`,
        })),
    [bookings],
  );

  const labelForVisit = (bookingId) =>
    visitOptions.find((v) => v.value === String(bookingId))?.label || '';

  const remove = async () => {
    try {
      await medicalRecordApi.remove(deleteTarget.id);
      success('تم حذف السجل');
      state.reload();
      onChange?.();
    } catch (err) {
      toastError(err.message || 'تعذر حذف السجل');
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="space-y-4">
      <Panel>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 text-base font-bold text-white">
              <Icon name="paperclip" size={18} className="text-rose-400" />
              سجلات خارجية
            </h3>
            <p className="mt-1 text-xs text-slate-400">
              ارفع تحاليلك وأشعتك وتقاريرك القديمة من أي عيادة أخرى لتظهر داخل سجلك الطبي وفي
              نسخته المطبوعة.
            </p>
          </div>

          <Button icon="plus" onClick={() => setEditing(emptyForm())}>
            إضافة سجل
          </Button>
        </div>
      </Panel>

      {state.loading && records.length === 0 ? (
        <Spinner variant="inline" label="جاري تحميل السجلات الخارجية..." />
      ) : records.length === 0 ? (
        <Panel>
          <EmptyState
            icon="paperclip"
            title="لا توجد سجلات خارجية"
            message="أضف أي تحليل أو أشعة أو تقرير قديم لتكتمل صورة سجلك الطبي."
            action={
              <Button icon="plus" onClick={() => setEditing(emptyForm())}>
                إضافة سجل
              </Button>
            }
          />
        </Panel>
      ) : (
        <div className="space-y-3">
          {records.map((record) => (
            <RecordCard
              key={record.id}
              record={record}
              visitLabel={labelForVisit(record.bookingId)}
              onEdit={() =>
                setEditing({
                  ...emptyForm(),
                  ...record,
                  recordDate: String(record.recordDate || '').slice(0, 10),
                  bookingId: record.bookingId ? String(record.bookingId) : '',
                  files: record.files || [],
                })
              }
              onDelete={() => setDeleteTarget(record)}
              onViewFile={setViewingFile}
            />
          ))}
        </div>
      )}

      {editing && (
        <RecordEditor
          form={editing}
          visitOptions={visitOptions}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            state.reload();
            onChange?.();
          }}
        />
      )}

      <MedicalFileViewer
        file={viewingFile}
        open={Boolean(viewingFile)}
        onClose={() => setViewingFile(null)}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={remove}
        destructive
        title="حذف السجل الخارجي"
        confirmLabel="حذف"
        message={`سيتم حذف "${deleteTarget?.title || ''}" وكل الملفات المرفقة به نهائياً.`}
      />
    </div>
  );
}

/* ========================================================================== */

function RecordCard({ record, visitLabel, onEdit, onDelete, onViewFile }) {
  const meta = typeMeta(record.recordType);
  const files = record.files || [];

  return (
    <Panel>
      <div className="flex flex-wrap items-start gap-3">
        <span className={`rounded-xl bg-white/5 p-2.5 ${meta.tone}`}>
          <Icon name={meta.icon} size={20} />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="min-w-0 break-words font-bold text-white">{record.title}</h4>
            <Badge className="border-white/10 bg-white/5 text-slate-300">{meta.label}</Badge>
          </div>

          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400">
            <span>{isoToDisplayDate(record.recordDate) || record.recordDate || '—'}</span>
            {/* The form's own example is «د. سمير»: a typed «د.» is not doubled. */}
            {record.externalDoctor && <span>د. {record.externalDoctor.replace(/^د\.\s*/, '')}</span>}
            {record.facility && <span>{record.facility}</span>}
            {visitLabel && (
              <span className="flex items-center gap-1 text-cyan-400">
                <Icon name="link" size={11} />
                {visitLabel}
              </span>
            )}
          </p>

          {record.notes && (
            <p className="mt-2 whitespace-pre-line text-xs leading-relaxed text-slate-300">
              {record.notes}
            </p>
          )}

          {files.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {files.map((file, index) => (
                <button
                  key={`${record.id}-${index}`}
                  type="button"
                  onClick={() => onViewFile(file)}
                  className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-slate-900 px-2.5 py-1.5 text-[11px] text-slate-300 transition-colors hover:border-cyan-500/50 hover:text-white"
                >
                  <Icon name="image" size={12} className="text-cyan-400" />
                  <span className="max-w-[160px] truncate">{file.name || `ملف ${index + 1}`}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex shrink-0 gap-1">
          <button
            type="button"
            onClick={onEdit}
            aria-label="تعديل"
            className="rounded-lg p-2 text-cyan-400 transition-colors hover:bg-cyan-500/20"
          >
            <Icon name="pencil" size={15} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            aria-label="حذف"
            className="rounded-lg p-2 text-red-400 transition-colors hover:bg-red-500/20"
          >
            <Icon name="trash-2" size={15} />
          </button>
        </div>
      </div>
    </Panel>
  );
}

/* ========================================================================== */

function RecordEditor({ form: initial, visitOptions, onClose, onSaved }) {
  const { success, error: toastError } = useNotifications();

  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [reading, setReading] = useState(false);

  const update = (key) => (event) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const addFiles = async (fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;

    if (form.files.length + incoming.length > MAX_FILES) {
      toastError(`الحد الأقصى ${MAX_FILES} ملفات لكل سجل`);
      return;
    }

    setReading(true);
    try {
      const read = [];
      for (const file of incoming) {
        // Images are compressed; anything else (a PDF report) is stored as-is,
        // because re-encoding it would corrupt it.
        const data = file.type.startsWith('image/')
          ? await compressImage(file, { maxWidth: 1600, quality: 0.78 })
          : await readFileAsDataUri(file);

        if (dataUriByteLength(data) > MAX_FILE_BYTES) {
          toastError(`"${file.name}" أكبر من ${formatBytes(MAX_FILE_BYTES)} بعد الضغط`);
          continue;
        }
        read.push({ data, name: file.name, type: file.type });
      }
      setForm((current) => ({ ...current, files: [...current.files, ...read] }));
    } catch (err) {
      toastError(`تعذر قراءة الملف: ${err.message}`);
    } finally {
      setReading(false);
    }
  };

  const validate = () => {
    const next = {};
    if (!form.title.trim()) next.title = 'اكتب عنواناً للسجل';
    if (form.recordDate && form.recordDate > todayKey()) {
      next.recordDate = 'تاريخ السجل في المستقبل';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        recordType: form.recordType,
        recordDate: form.recordDate || null,
        externalDoctor: form.externalDoctor.trim() || null,
        facility: form.facility.trim() || null,
        bookingId: form.bookingId || null,
        notes: form.notes.trim() || null,
        files: form.files,
      };

      if (form.id) await medicalRecordApi.update(form.id, payload);
      else await medicalRecordApi.create(payload);

      success(form.id ? 'تم تحديث السجل' : 'تمت إضافة السجل لسجلك الطبي');
      onSaved();
    } catch (err) {
      toastError(err.message || 'تعذر حفظ السجل');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={form.id ? 'تعديل سجل خارجي' : 'إضافة سجل خارجي'}
      icon="paperclip"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            إلغاء
          </Button>
          <Button icon="save" loading={saving} onClick={submit}>
            حفظ في سجلي الطبي
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="عنوان السجل" error={errors.title} required>
            <Input
              value={form.title}
              onChange={update('title')}
              placeholder="مثال: أشعة مقطعية على الصدر"
            />
          </Field>

          <Field label="نوع السجل">
            <Select value={form.recordType} onChange={update('recordType')}>
              {RECORD_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="تاريخ السجل"
            error={errors.recordDate}
            hint="التاريخ المكتوب على المستند، وليس تاريخ الرفع"
          >
            <DateInput max={todayKey()} value={form.recordDate} onChange={update('recordDate')} />
          </Field>

          <Field label="الجهة / المعمل" hint="اختياري">
            <Input value={form.facility} onChange={update('facility')} placeholder="مركز النيل للأشعة" />
          </Field>

          <Field label="الطبيب الذي طلبه" hint="اختياري">
            <Input value={form.externalDoctor} onChange={update('externalDoctor')} placeholder="د. سمير" />
          </Field>

          <Field
            label="ربط بزيارة سابقة"
            hint={visitOptions.length ? 'اختياري' : 'لا توجد زيارات سابقة بعد'}
          >
            <Select
              value={form.bookingId}
              onChange={update('bookingId')}
              disabled={visitOptions.length === 0}
            >
              <option value="">غير مرتبط بزيارة</option>
              {visitOptions.map((visit) => (
                <option key={visit.value} value={visit.value}>
                  {visit.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="التفاصيل والملاحظات" hint="اكتب ما تعرفه عن هذا المستند — سيظهر مع السجل">
          <Textarea
            rows={4}
            value={form.notes}
            onChange={update('notes')}
            placeholder="مثال: عملت الأشعة قبل الجراحة، والنتيجة كانت سليمة."
          />
        </Field>

        {/* Attachments */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-bold text-slate-200">الملفات المرفقة</p>
            <span className="text-[11px] text-slate-500">
              {form.files.length} / {MAX_FILES}
            </span>
          </div>

          <label
            className={`flex min-h-[72px] cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed p-4 text-sm transition-all ${
              reading
                ? 'border-cyan-500 bg-cyan-500/10 text-cyan-300'
                : 'border-white/20 text-slate-400 hover:border-cyan-500/50 hover:text-cyan-400'
            }`}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              addFiles(event.dataTransfer.files);
            }}
          >
            <Icon name={reading ? 'loader-2' : 'image-plus'} size={20} className={reading ? 'animate-spin' : ''} />
            {reading ? 'جاري قراءة الملفات...' : 'اسحب الصور أو الملفات هنا، أو اضغط للاختيار'}
            <input
              type="file"
              accept="image/*,application/pdf"
              multiple
              className="hidden"
              onChange={(event) => {
                addFiles(event.target.files);
                event.target.value = '';
              }}
            />
          </label>

          {form.files.length > 0 && (
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {form.files.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="relative overflow-hidden rounded-xl border border-white/10 bg-slate-900"
                >
                  {String(file.data).startsWith('data:image/') ? (
                    <img src={file.data} alt="" className="h-24 w-full object-cover" />
                  ) : (
                    <div className="flex h-24 items-center justify-center text-slate-500">
                      <Icon name="file-text" size={26} />
                    </div>
                  )}
                  <p className="truncate px-2 py-1.5 text-[10px] text-slate-400">{file.name}</p>
                  <button
                    type="button"
                    aria-label={`إزالة ${file.name}`}
                    onClick={() =>
                      setForm((current) => ({
                        ...current,
                        files: current.files.filter((_, i) => i !== index),
                      }))
                    }
                    className="absolute end-1 top-1 rounded-lg bg-black/70 p-1 text-red-300 hover:bg-black/90"
                  >
                    <Icon name="x" size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <p className="mt-2 text-[11px] text-slate-500">
            الصور تُضغط تلقائياً قبل الرفع. ملفات PDF تُحفظ كما هي لكنها لا تظهر داخل النسخة
            المطبوعة — يُذكر اسمها فقط.
          </p>
        </div>
      </div>
    </Modal>
  );
}

export default ExternalRecords;
